// Helpers for the external identity provider (OpenID Connect) login: authorization code + PKCE.

// Pending authorization request, kept in sessionStorage so it only lives in the tab that started it.
const FLOW_STORAGE_KEY = "idpFlow";

// Data needed to end the IdP session on logout, kept while the Consuman session is alive.
const SESSION_STORAGE_KEY = "idpSession";

const base64UrlEncode = bytes =>
  btoa(String.fromCharCode(...new Uint8Array(bytes)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");

const randomString = (byteLength = 32) => {
  const bytes = new Uint8Array(byteLength);
  window.crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
};

const createCodeChallenge = async codeVerifier => {
  const digest = await window.crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(codeVerifier)
  );
  return base64UrlEncode(digest);
};

/*
  Builds the IdP authorization URL from the data returned by the start endpoint and stores the
  state, nonce and PKCE verifier the callback page needs to complete the login.
*/
export const buildAuthorizationUrl = async (start, rememberMe) => {
  const state = randomString();
  const nonce = randomString();
  const codeVerifier = randomString(48);
  const codeChallenge = await createCodeChallenge(codeVerifier);

  window.sessionStorage.setItem(
    FLOW_STORAGE_KEY,
    JSON.stringify({
      state,
      nonce,
      codeVerifier,
      enterpriseId: start.enterpriseId,
      enterpriseCode: start.enterpriseCode,
      rememberMe,
    })
  );

  const url = new URL(start.authorizationEndpoint);

  url.searchParams.set("response_type", "code");
  url.searchParams.set("client_id", start.clientId);
  url.searchParams.set("redirect_uri", start.redirectUri);
  url.searchParams.set("scope", start.scopes);
  url.searchParams.set("state", state);
  url.searchParams.set("nonce", nonce);
  url.searchParams.set("code_challenge", codeChallenge);
  url.searchParams.set("code_challenge_method", "S256");

  return url.toString();
};

export const getPendingFlow = () => {
  try {
    return JSON.parse(window.sessionStorage.getItem(FLOW_STORAGE_KEY) ?? "null");
  } catch {
    return null;
  }
};

export const clearPendingFlow = () => window.sessionStorage.removeItem(FLOW_STORAGE_KEY);

export const setIdentityProviderSession = session =>
  window.localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));

export const getIdentityProviderSession = () => {
  try {
    return JSON.parse(window.localStorage.getItem(SESSION_STORAGE_KEY) ?? "null");
  } catch {
    return null;
  }
};

export const clearIdentityProviderSession = () =>
  window.localStorage.removeItem(SESSION_STORAGE_KEY);

/*
  RP initiated logout URL. Returns null when the IdP does not advertise an end_session_endpoint,
  in which case only the Consuman session is closed.
*/
export const buildEndSessionUrl = session => {
  if (!session?.endSessionEndpoint) return null;

  const url = new URL(session.endSessionEndpoint);

  if (session.idToken) url.searchParams.set("id_token_hint", session.idToken);
  url.searchParams.set("client_id", session.clientId);
  url.searchParams.set("post_logout_redirect_uri", session.logoutUri);

  return url.toString();
};
